import { $isHeadingNode } from '@lexical/rich-text';
import { $getRoot, $isParagraphNode, EditorState } from 'lexical';
import * as BrandDefinition from 'modules/BrandDefinition';
import * as Constants from 'const';
import * as Models from 'models';
import { Schemas } from 'services/ArtboardConverter/models';
import { $isCustomHeadingNode } from '../nodes/CustomHeadingNode';
import { $isCustomListNode } from '../nodes/CustomListNode';
import { $isCustomParagraphNode } from '../nodes/CustomParagraphNode';
import { $isNoWrapNode } from '../nodes/NoWrapNode/NoWrapNode';
import { createFlatList } from './createFlatList';
import { createSegments } from './createSegments';
import { createParagraphForText, createParagraphForListItem, createParagraphForList } from './factories';

export type ArtboardConverterStyles = {
  color: string;
  partColor: string;
  bulletColor: string;
  fontSize: number;
  fontFamily: string;
  partFontFamily?: string;
  lineHeight: number;
  paragraphAlign?: string;
};

type ArtboardConverterOptions = {
  projectType: Constants.ProjectType;
  colors: Models.BrandColorsList;
  fonts: Models.BrandFontsList;
  brandStyle?: Models.BrandStyleMap;
  surfaces: Models.ScreensOrderedMap;
  paragraphLineHeightNoPx?: boolean;
};

export function convertLexicalEditorState(
  editorState: EditorState,
  options: ArtboardConverterOptions,
  converterStyles: ArtboardConverterStyles,
  isLayoutPreview = false,
): Schemas.CombinedParagraph[] {
  const { projectType, paragraphLineHeightNoPx, surfaces, colors, fonts, brandStyle } = options;

  return editorState.read(() => {

    const paragraphs: (Schemas.Paragraph | Schemas.ListParagraph)[] = [];
    $getRoot().getChildren().forEach((node) => {

      if ($isParagraphNode(node) || $isNoWrapNode(node) || $isHeadingNode(node)) {
        const segments = createSegments(
          node.getChildren(),
          { surfaces },
          {
            color: converterStyles.partColor,
            fontSize: converterStyles.fontSize,
            fontFamily: converterStyles.fontFamily,
            lineHeight: (($isCustomParagraphNode(node) || $isCustomHeadingNode(node)) ? node.getLineHeight() : undefined)
              ??
              converterStyles.lineHeight,
            bulletColor: converterStyles.bulletColor,
            colors, fonts, brandStyle,
          },
          isLayoutPreview,
        );
        paragraphs.push(createParagraphForText(
          node,
          { projectType, lineheightNoPx: paragraphLineHeightNoPx },
          converterStyles,
          segments,
        ));

        return;
      }

      if ($isCustomListNode(node)) {
        const bulletColorRef = node.getColor();
        const bulletColor = bulletColorRef ? BrandDefinition.getBrandColorValueHEX(colors, bulletColorRef) : undefined;
        const nestedParagraphs = createFlatList(node).map(
          ({ item, children, depth }): Schemas.ListItemParagraph => {
            const segments = createSegments(
              children,
              { surfaces },
              {
                color: converterStyles.partColor,
                fontSize: converterStyles.fontSize,
                fontFamily: converterStyles.fontFamily,
                lineHeight: item.getLineHeight() ?? converterStyles.lineHeight,
                bulletColor: bulletColor ?? converterStyles.bulletColor,
                colors, fonts, brandStyle,
              },
              isLayoutPreview,
            );

            return createParagraphForListItem(
              item,
              { projectType, lineheightNoPx: paragraphLineHeightNoPx, depth, bulletColor },
              converterStyles,
              segments,
            );
          },
        );
        paragraphs.push(createParagraphForList(node, projectType, converterStyles, nestedParagraphs));

        return;
      }

      throw Error(`Wrong node type: ${node.getType()}`);
    });

    return paragraphs;
  });
}
